import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, inject } from '@angular/core';
import {
  ManagedUserPermissionsForm,
  PermissionsFormControlNames,
  formControlsToPermissionsEnumMap,
} from '../../../core/forms/managed-user.form';
import { UserPermissionInfoInterface, UserPermissionOptionsInterface } from '../../../core/models/user-permissions.model';
import { UserService } from '../../../core/services/user/user.service';
import { SharedModule } from '../../../shared/shared.module';

type ControlsGroupName =
  'catalogue' | 'offers' | 'orders' | 'projectReservations' | 'cmpr' | 'claims' | 'infoPages' | 'ordersLeadTime' | 'companyProfile';

@Component({
  selector: 'app-user-permissions',
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './user-permissions.component.html',
})
export class UserPermissionsComponent implements OnInit, OnChanges {
  private userService = inject(UserService);

  @Input() permissionsForm: ManagedUserPermissionsForm;
  @Input() permissionOptions: UserPermissionOptionsInterface;

  controlGroups: Record<ControlsGroupName, PermissionsFormControlNames[]>;
  dependenciesMap: Partial<Record<PermissionsFormControlNames, PermissionsFormControlNames[]>> = {};
  infoPagesPermissions: UserPermissionInfoInterface[] = [];
  hiddenControlNames: PermissionsFormControlNames[] = [];
  userCompanyHasDiscount: boolean;

  ngOnChanges(): void {
    this.hiddenControlNames = this.getHiddenControlNames();
    this.infoPagesPermissions = this.permissionOptions.informationPages.filter((permissionInfo) =>
      this.isControlVisible(permissionInfo.value),
    );

    // controlGroups wait for the company's discount, loaded in ngOnInit
    if (this.controlGroups) {
      this.controlGroups = this.getVisibleControlGroups();
    }
  }

  ngOnInit() {
    this.userService.getUserCompany().subscribe(({ discount }) => {
      this.userCompanyHasDiscount = !!discount;

      const discountPermissionControlName = this.userCompanyHasDiscount ? ['showPricelistDiscount'] : [];

      this.controlGroups = this.getVisibleControlGroups();

      this.dependenciesMap = {
        showPrices: ['viewCatalogue'],
        showPricelistDiscount: ['viewCatalogue', 'showPrices'],
        viewAndCreateOffers: ['viewCatalogue'],
        createOrders: ['viewCatalogue', 'showPrices', ...discountPermissionControlName, 'viewAndCreateOffers'],
        viewOrders: ['viewCatalogue', 'showPrices', ...discountPermissionControlName],
        viewOrdersOnCompanyLevel: ['viewCatalogue', 'showPrices', ...discountPermissionControlName, 'viewOrders'],
        viewAndCreateProjectReservations: ['viewCatalogue', 'showPrices', ...discountPermissionControlName],
        viewProjectReservationsOnCompanyLevel: [
          'viewCatalogue',
          'showPrices',
          ...discountPermissionControlName,
          'viewAndCreateProjectReservations',
        ],
        viewCmprs: ['viewCatalogue', 'showPrices', ...discountPermissionControlName],
        createCmprs: ['viewCatalogue', 'showPrices', ...discountPermissionControlName, 'viewCmprs'],
        viewCmprsOnCompanyLevel: ['viewCatalogue', 'showPrices', ...discountPermissionControlName, 'viewCmprs'],
        viewAndCreateClaims: ['viewCatalogue', 'showPrices', ...discountPermissionControlName],
        viewClaimsOnCompanyLevel: ['viewCatalogue', 'showPrices', ...discountPermissionControlName, 'viewAndCreateClaims'],
      };

      this.updateControlsState();

      this.permissionsForm.valueChanges.subscribe(() => this.updateControlsState());
    });
  }

  private getVisibleControlGroups(): Record<ControlsGroupName, PermissionsFormControlNames[]> {
    const discountPermissionControlName = this.userCompanyHasDiscount ? ['showPricelistDiscount'] : [];

    return this.removeHiddenControls({
      catalogue: ['viewCatalogue', 'showPrices', ...discountPermissionControlName],
      offers: ['viewAndCreateOffers', 'createOrders'],
      orders: ['viewOrders', 'viewOrdersOnCompanyLevel'],
      projectReservations: ['viewAndCreateProjectReservations', 'viewProjectReservationsOnCompanyLevel'],
      cmpr: ['viewCmprs', 'createCmprs', 'viewCmprsOnCompanyLevel'],
      claims: ['viewAndCreateClaims', 'viewClaimsOnCompanyLevel'],
      infoPages: this.infoPagesPermissions.map((permissionInfo) => permissionInfo.value),
      ordersLeadTime: ['viewEstimatedLeadTimePage'],
      companyProfile: ['viewCompanyInformation', 'viewPurchaseAndSalesAgreement'],
    });
  }

  private getHiddenControlNames(): PermissionsFormControlNames[] {
    const missingPermissions = this.permissionOptions.granted
      .filter((permission) => permission.disabled)
      .map((permission) => permission.value);

    const hiddenPermissionControlNames = Object.keys(formControlsToPermissionsEnumMap).filter((controlName) =>
      missingPermissions.includes(formControlsToPermissionsEnumMap[controlName]),
    );
    const hiddenInfoPageControlNames = this.permissionOptions.informationPages
      .filter((permissionInfo) => permissionInfo.disabled)
      .map((permissionInfo) => permissionInfo.value);

    return [...hiddenPermissionControlNames, ...hiddenInfoPageControlNames];
  }

  private removeHiddenControls(
    controlNamesByGroup: Record<ControlsGroupName, PermissionsFormControlNames[]>,
  ): Record<ControlsGroupName, PermissionsFormControlNames[]> {
    const visibleEntries = Object.entries(controlNamesByGroup).map(([group, controlNames]) => [
      group,
      controlNames.filter((controlName) => this.isControlVisible(controlName)),
    ]);

    return Object.fromEntries(visibleEntries);
  }

  private updateControlsState(): void {
    Object.entries(this.permissionsForm.controls).forEach(([name, control]) => {
      const dependencies = this.dependenciesMap[name];

      if (!dependencies) {
        return;
      }

      const shouldBeEnabled = dependencies.every((depName) => this.permissionsForm.controls[depName].value);

      if (shouldBeEnabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
        control.setValue(false, { emitEvent: false });
      }
    });
  }

  isControlVisible(controlName: PermissionsFormControlNames): boolean {
    return !this.hiddenControlNames.includes(controlName);
  }

  isGroupVisible(group: ControlsGroupName): boolean {
    return this.controlGroups?.[group].length > 0;
  }

  setControlsGroupValue(group: ControlsGroupName, checked: boolean): void {
    this.controlGroups[group].forEach((controlName) => {
      this.permissionsForm.controls[controlName].setValue(checked, { emitEvent: false });
    });

    this.updateControlsState();
  }

  disableAllAvailable(group: ControlsGroupName): boolean {
    return this.controlGroups[group].every((controlName) => this.permissionsForm.controls[controlName].value);
  }

  enableAllAvailable(group: ControlsGroupName): boolean {
    const groupControls = this.controlGroups[group];
    const groupDependencies = new Set<PermissionsFormControlNames>();

    groupControls.forEach((controlName) => {
      const controlDependencies = this.dependenciesMap[controlName];

      if (!controlDependencies) {
        return;
      }

      controlDependencies.forEach((dependencyControlName) => {
        if (!groupControls.includes(dependencyControlName)) {
          groupDependencies.add(dependencyControlName);
        }
      });
    });

    const allDependenciesAreChecked = [...groupDependencies].every((controlName) => this.permissionsForm.controls[controlName].value);

    return allDependenciesAreChecked;
  }
}
